import { useState, useCallback, useRef } from 'react';
import { useRouter, usePathname, useSearchParams } from '@/lib/app-router';
import { useAuthStore } from '@/stores/user.store';
import { MY_ACCOUNT_ROUTES } from '@/routes/routes.constants';
import { logger } from '@/lib/logger';

interface UseUserMenuOptions {
  onAccessCardQr?: () => void;
  onScanTicket?: () => void;
  onLoyaltyQr?: () => void;
}

/**
 * Matches a Zones lane route (`/z/{laneNumber}`). The pathname is already stripped of any
 * `/c/{companyId}` base by `usePathname`, so the prefix check is base-path-agnostic.
 */
const ZONE_ROUTE_PATTERN = /^\/z\/[^/]+/;

/** Public landing/home page (base-path-stripped, like the zone pattern above). */
const HOME_ROUTE = '/';

export function useUserMenu(options?: UseUserMenuOptions) {
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const logout = useAuthStore((s) => s.logout);

  const toggle = useCallback(() => setIsOpen((prev) => !prev), []);
  const close = useCallback(() => setIsOpen(false), []);

  const handleProfileClick = useCallback(() => {
    close();
    router.push(MY_ACCOUNT_ROUTES.profile);
  }, [close, router]);

  const handleOverviewClick = useCallback(() => {
    close();
    router.push(MY_ACCOUNT_ROUTES.overview);
  }, [close, router]);

  const handleChangeAppearanceClick = useCallback(() => {
    close();
    router.push(MY_ACCOUNT_ROUTES.settings);
  }, [close, router]);

  const handleLogoutClick = useCallback(async () => {
    close();
    await logout();
    // VEC-1776: when logging out from a Zones session (`/z/{lane}`), return to that zone's
    // sign-in page rather than the generic ecommerce login, so the user lands back where they
    // scanned in. The lane + any `d`/`companyId` query params are preserved; the app-router
    // wrapper re-applies the `/c/{companyId}` base on replace.
    if (ZONE_ROUTE_PATTERN.test(pathname)) {
      const query = searchParams.toString();
      router.replace(query ? `${pathname}?${query}` : pathname);
      return;
    }
    // Logging out lands on Home, never Sign In: the guest has somewhere to go from
    // there, and a login screen reads as "you must sign in again". The
    // `/c/{companyId}` base is re-applied by the app-router wrapper.
    router.replace(HOME_ROUTE);
  }, [close, logout, router, pathname, searchParams]);

  const handleAccessCardQrClick = useCallback(() => {
    close();
    if (options?.onAccessCardQr) {
      options.onAccessCardQr();
    } else {
      logger.info('Show access card QR clicked (placeholder)', {
        feature: 'user-menu',
        route: 'accessCardQr',
      });
    }
  }, [close, options?.onAccessCardQr]);

  const handleLoyaltyQrClick = useCallback(() => {
    close();
    if (options?.onLoyaltyQr) {
      options.onLoyaltyQr();
    } else {
      logger.info('Show loyalty QR clicked (placeholder)', {
        feature: 'user-menu',
        route: 'loyaltyQr',
      });
    }
  }, [close, options?.onLoyaltyQr]);

  const handleScanTicketClick = useCallback(() => {
    close();
    if (options?.onScanTicket) {
      options.onScanTicket();
    } else {
      logger.info('Scan ticket clicked (placeholder)', {
        feature: 'user-menu',
        route: 'scanTicket',
      });
    }
  }, [close, options?.onScanTicket]);

  return {
    isOpen,
    anchorRef,
    toggle,
    close,
    handleProfileClick,
    handleOverviewClick,
    handleChangeAppearanceClick,
    handleLogoutClick,
    handleAccessCardQrClick,
    handleLoyaltyQrClick,
    handleScanTicketClick,
  };
}
